import React, { useState, useEffect } from 'react';
import {
    Card,
    Button,
    Row,
    Col,
    Image,
    Pagination,
    message,
    Modal,
    Space,
    Typography,
    Empty,
    Spin,
    Radio,
    Tag,
    Tooltip,
    Select
} from 'antd';
import { DeleteOutlined, PictureOutlined, ExclamationCircleOutlined, TagOutlined, PlusOutlined, EditOutlined, CloudUploadOutlined, CloudDownloadOutlined, CheckOutlined, DesktopOutlined } from '@ant-design/icons';
import { ApiService } from '../services/api';
import type { Tag as TagType, SimpleTag, Image as ImageType, AdminImageListRequest, SortCategory, BatchDeleteImageRequest } from '../types';

const { Title, Text } = Typography;
const { Option } = Select;

const ImageList: React.FC = () => {
    const [images, setImages] = useState<ImageType[]>([]);
    const [tags, setTags] = useState<TagType[]>([]);
    const [sortCategories, setSortCategories] = useState<SortCategory[]>([]);
    const [deviceTypes, setDeviceTypes] = useState<Array<{ id: number, width: number; height: number; description: string }>>([]);
    const [loading, setLoading] = useState(false);
    const [selectedSize, setSelectedSize] = useState<{ width: number; height: number } | null>(null);
    const [selectedTagId, setSelectedTagId] = useState<number | undefined>(undefined);
    const [selectedAnimated, setSelectedAnimated] = useState<boolean | undefined>(undefined);
    const [selectedDistributed, setSelectedDistributed] = useState<boolean | undefined>(undefined);
    const [selectedMultiScreen, setSelectedMultiScreen] = useState<boolean | undefined>(undefined);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(50);
    const [total, setTotal] = useState(0);
    const [imageHost, setImageHost] = useState('');

    // 新增：删除确认Modal的状态
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [imageToDelete, setImageToDelete] = useState<number | null>(null);
    const [deleting, setDeleting] = useState(false);

    // 标签管理相关状态
    const [tagModalOpen, setTagModalOpen] = useState(false);
    const [currentImageId, setCurrentImageId] = useState<number | null>(null);
    const [currentImageTags, setCurrentImageTags] = useState<TagType[]>([]);
    const [managingTags, setManagingTags] = useState(false);

    // 新增：排序类别管理相关状态
    const [sortCategoryModalOpen, setSortCategoryModalOpen] = useState(false);
    const [currentImageSortCategory, setCurrentImageSortCategory] = useState<SortCategory | null>(null);
    const [managingSortCategory, setManagingSortCategory] = useState(false);

    // 批量选择相关状态
    const [selectedImageIds, setSelectedImageIds] = useState<number[]>([]);
    const [batchSelectMode, setBatchSelectMode] = useState(false);
    const [batchDeleteModalOpen, setBatchDeleteModalOpen] = useState(false);
    const [batchDeleting, setBatchDeleting] = useState(false);



    // 加载标签列表
    const loadTags = async () => {
        try {
            const response = await ApiService.getTagList({ language: 'zh' });
            if (response.code === 200) {
                setTags(response.data.tags);
            } else {
                message.error(response.message || '获取标签列表失败');
            }
        } catch (error) {
            message.error('获取标签列表失败');
            console.error('获取标签列表失败:', error);
        }
    };

    // 加载设备类型列表
    const loadDeviceTypes = async () => {
        try {
            const response = await ApiService.getDeviceTypeList({ page: 1, page_size: 100 });
            if (response.code === 200) {
                const types = response.data.device_types.map(type => ({
                    id: type.id,
                    width: type.width,
                    height: type.height,
                    description: type.description
                }));
                setDeviceTypes(types);
            } else {
                message.error(response.message || '获取设备类型列表失败');
            }
        } catch (error) {
            message.error('获取设备类型列表失败');
            console.error('获取设备类型列表失败:', error);
        }
    };

    // 加载排序类别列表
    const loadSortCategories = async () => {
        try {
            const response = await ApiService.getSortCategoryList({ page: 1, page_size: 100 });
            if (response.code === 200) {
                const sortedCategories = response.data.sort_categories.sort((a: any, b: any) => a.sort_index - b.sort_index);
                setSortCategories(sortedCategories);
            } else {
                message.error(response.message || '获取排序类别列表失败');
            }
        } catch (error) {
            message.error('获取排序类别列表失败');
            console.error('获取排序类别列表失败:', error);
        }
    };

    // 加载图片列表
    const loadImages = async () => {
        if (!selectedSize) {
            setImages([]);
            setTotal(0);
            return;
        }

        setLoading(true);
        try {
            const request: AdminImageListRequest = {
                width: selectedSize.width,
                height: selectedSize.height,
                page: currentPage,
                page_size: pageSize,
            };

            // 添加可选筛选条件
            // multi_screen 优先级高于 tag_id，如果选择了双屏素材，则不添加 tag_id 参数
            if (selectedMultiScreen !== undefined && selectedMultiScreen === true) {
                request.multi_screen = true;
            } else {
                // 只有在不是双屏素材筛选时，才添加 tag_id 参数
                if (selectedTagId !== undefined && selectedTagId !== null) {
                    request.tag_id = selectedTagId;
                }
            }
            if (selectedAnimated !== undefined) {
                request.is_animated = selectedAnimated;
            }
            if (selectedDistributed !== undefined) {
                request.is_distributed = selectedDistributed;
            }

            const response = await ApiService.getAdminImageList(request);
            if (response.code === 200) {
                // 确保images是一个数组，防止null或undefined导致的错误
                const images = response.data.images || [];
                setImages(images);
                setTotal(response.data.pagination?.total || 0);
                setImageHost(response.data.image_host || '');
            } else {
                message.error(response.message || '获取图片列表失败');
                setImages([]);
                setTotal(0);
            }
        } catch (error) {
            message.error('获取图片列表失败');
            console.error('获取图片列表失败:', error);
            setImages([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    // 删除图片
    const handleDeleteImage = (imageId: number) => {
        console.log('handleDeleteImage called with imageId:', imageId);
        setImageToDelete(imageId);
        setDeleteModalOpen(true);
    };

    // 确认删除操作
    const confirmDelete = async () => {
        if (!imageToDelete) return;

        setDeleting(true);
        try {
            // 检查token
            const token = localStorage.getItem('token');
            console.log('Token存在:', !!token);
            console.log('开始删除图片，ID:', imageToDelete);

            const response = await ApiService.deleteImage({ image_id: imageToDelete });
            console.log('删除响应:', response);

            if (response.code === 200) {
                message.success('图片删除成功');
                // 重新加载当前页面的图片
                await loadImages();
                setDeleteModalOpen(false);
                setImageToDelete(null);
            } else {
                console.error('删除失败响应:', response);
                message.error(response.message || '删除图片失败');
            }
        } catch (error: any) {
            console.error('删除图片网络错误:', error);
            console.error('错误详情:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                statusText: error.response?.statusText
            });

            if (error.response?.status === 401) {
                message.error('认证失败，请重新登录');
            } else if (error.response?.status === 403) {
                message.error('权限不足，无法删除图片');
            } else {
                message.error(`删除图片失败: ${error.message || '网络错误'}`);
            }
        } finally {
            setDeleting(false);
        }
    };

    // 取消删除
    const cancelDelete = () => {
        setDeleteModalOpen(false);
        setImageToDelete(null);
        console.log('用户取消了删除操作');
    };

    // 打开标签管理Modal
    const handleManageTags = (imageId: number, imageTags: SimpleTag[] = []) => {
        setCurrentImageId(imageId);
        // 将SimpleTag[]转换为TagType[]，需要从全局tags中找到完整信息
        const fullTags = imageTags.map(imageTag => {
            const fullTag = tags.find(tag => tag.id === imageTag.id);
            return fullTag || {
                id: imageTag.id,
                tag: imageTag.tag,
                remark: imageTag.remark,
                l10n_ar: '',
                l10n_de: '',
                l10n_en: '',
                l10n_es: '',
                l10n_es_mx: '',
                l10n_fr: '',
                l10n_it: '',
                l10n_ja: '',
                l10n_ko: '',
                l10n_ne: '',
                l10n_nl: '',
                l10n_pt: '',
                l10n_ru: '',
                l10n_th: '',
                l10n_tr: '',
                l10n_vi: '',
                l10n_zh: imageTag.remark,
                l10n_zh_tw: '',
                l10n_he: ''
            };
        });
        setCurrentImageTags(fullTags);
        setTagModalOpen(true);
    };

    // 为图片添加标签 - 直接更新本地状态
    const handleAddTag = async (tagId: number) => {
        if (!currentImageId) return;

        setManagingTags(true);
        try {
            const response = await ApiService.addImageTag({
                image_id: currentImageId,
                tag_id: tagId
            });

            if (response.code === 200) {
                message.success('标签添加成功');
                // 直接更新本地状态，不重新加载
                const addedTag = tags.find(tag => tag.id === tagId);
                if (addedTag) {
                    setImages(prevImages =>
                        prevImages.map(img =>
                            img.id === currentImageId
                                ? {
                                    ...img,
                                    tags: [...(img.tags || []), {
                                        id: addedTag.id,
                                        tag: addedTag.tag,
                                        remark: addedTag.remark
                                    }]
                                }
                                : img
                        )
                    );
                    setCurrentImageTags(prev => [...prev, addedTag]);
                }
            } else {
                message.error(response.message || '标签添加失败');
            }
        } catch (error: any) {
            message.error('标签添加失败');
            console.error('标签添加失败:', error);
        } finally {
            setManagingTags(false);
        }
    };

    // 删除图片标签 - 直接更新本地状态
    const handleRemoveTag = async (tagId: number) => {
        if (!currentImageId) return;

        setManagingTags(true);
        try {
            const response = await ApiService.removeImageTag({
                image_id: currentImageId,
                tag_id: tagId
            });

            if (response.code === 200) {
                message.success('标签删除成功');
                // 直接更新本地状态，不重新加载
                setImages(prevImages =>
                    prevImages.map(img =>
                        img.id === currentImageId
                            ? {
                                ...img,
                                tags: (img.tags || []).filter(tag => tag.id !== tagId)
                            }
                            : img
                    )
                );
                setCurrentImageTags(prev => prev.filter(tag => tag.id !== tagId));
            } else {
                message.error(response.message || '标签删除失败');
            }
        } catch (error: any) {
            message.error('标签删除失败');
            console.error('标签删除失败:', error);
        } finally {
            setManagingTags(false);
        }
    };

    // 关闭标签管理Modal
    const closeTagModal = () => {
        setTagModalOpen(false);
        setCurrentImageId(null);
        setCurrentImageTags([]);
    };

    // 新增：打开排序类别管理Modal
    const handleManageSortCategory = (imageId: number, sortCategory: SortCategory | null = null) => {
        setCurrentImageId(imageId);
        setCurrentImageSortCategory(sortCategory);
        setSortCategoryModalOpen(true);
    };

    // 新增：修改图片排序类别 - 直接更新本地状态
    const handleUpdateSortCategory = async (sortCategoryId: number) => {
        if (!currentImageId) return;

        setManagingSortCategory(true);
        try {
            const response = await ApiService.updateImageSortCategory({
                image_id: currentImageId,
                sort_category_id: sortCategoryId
            });

            if (response.code === 200) {
                message.success('排序类别修改成功');
                // 直接更新本地状态，不重新加载
                const updatedCategory = sortCategories.find(category => category.id === sortCategoryId);
                setImages(prevImages =>
                    prevImages.map(img =>
                        img.id === currentImageId
                            ? { ...img, sort_category: updatedCategory }
                            : img
                    )
                );
                setCurrentImageSortCategory(updatedCategory || null);
                // 关闭Modal
                closeSortCategoryModal();
            } else {
                message.error(response.message || '排序类别修改失败');
            }
        } catch (error: any) {
            message.error('排序类别修改失败');
            console.error('排序类别修改失败:', error);
        } finally {
            setManagingSortCategory(false);
        }
    };

    // 新增：关闭排序类别管理Modal
    const closeSortCategoryModal = () => {
        setSortCategoryModalOpen(false);
        setCurrentImageId(null);
        setCurrentImageSortCategory(null);
    };

    // 批量选择相关功能
    const toggleBatchSelectMode = () => {
        setBatchSelectMode(!batchSelectMode);
        setSelectedImageIds([]);
    };

    const handleImageSelect = (imageId: number) => {
        if (selectedImageIds.includes(imageId)) {
            setSelectedImageIds(selectedImageIds.filter(id => id !== imageId));
        } else {
            setSelectedImageIds([...selectedImageIds, imageId]);
        }
    };

    const selectAllImages = () => {
        if (selectedImageIds.length === images.length) {
            setSelectedImageIds([]);
        } else {
            setSelectedImageIds(images.map(img => img.id));
        }
    };

    const handleBatchDelete = () => {
        if (selectedImageIds.length === 0) {
            message.warning('请先选择要删除的图片');
            return;
        }
        setBatchDeleteModalOpen(true);
    };

    const confirmBatchDelete = async () => {
        if (selectedImageIds.length === 0) return;

        setBatchDeleting(true);
        try {
            const response = await ApiService.batchDeleteImages({ image_ids: selectedImageIds });

            if (response.code === 200) {
                const { success_count, failed_count, results } = response.data;

                // 显示批量删除结果
                if (failed_count === 0) {
                    message.success(`成功删除 ${success_count} 张图片`);
                } else {
                    message.warning(`删除完成：成功 ${success_count} 张，失败 ${failed_count} 张`);

                    // 显示失败的详细信息
                    const failedResults = results.filter(r => !r.success);
                    failedResults.forEach(result => {
                        console.warn(`图片 ${result.image_id} 删除失败: ${result.error}`);
                    });
                }

                // 重新加载图片列表
                await loadImages();
                setSelectedImageIds([]);
                setBatchDeleteModalOpen(false);
                setBatchSelectMode(false);
            } else {
                message.error(response.message || '批量删除失败');
            }
        } catch (error: any) {
            console.error('批量删除失败:', error);
            message.error(`批量删除失败: ${error.message || '网络错误'}`);
        } finally {
            setBatchDeleting(false);
        }
    };

    const cancelBatchDelete = () => {
        setBatchDeleteModalOpen(false);
    };



    // 新增：切换分发状态 - 直接操作无需确认
    const handleToggleDistribution = async (imageId: number, currentDistribution: boolean) => {
        const newDistributionStatus = !currentDistribution;
        try {
            const response = await ApiService.updateImageDistribution({
                image_id: imageId,
                is_distributed: newDistributionStatus
            });

            if (response.code === 200) {
                message.success(response.data.message || `图片${newDistributionStatus ? '上架' : '下架'}成功`);
                // 直接更新本地状态，不重新加载
                setImages(prevImages =>
                    prevImages.map(img =>
                        img.id === imageId
                            ? { ...img, is_distributed: newDistributionStatus }
                            : img
                    )
                );
            } else {
                message.error(response.message || `分发状态修改失败`);
            }
        } catch (error: any) {
            message.error(`分发状态修改失败`);
            console.error('分发状态修改失败:', error);
        }
    };

    // 新增：切换双屏素材状态 - 直接操作无需确认
    const handleToggleMultiScreen = async (imageId: number, currentMultiScreen: boolean) => {
        const newMultiScreenStatus = !currentMultiScreen;
        try {
            const response = await ApiService.updateImageMultiScreen({
                image_id: imageId,
                multi_screen: newMultiScreenStatus
            });

            if (response.code === 200) {
                message.success(response.data.message || `图片${newMultiScreenStatus ? '设置为双屏素材' : '取消双屏素材'}成功`);
                // 直接更新本地状态，不重新加载
                setImages(prevImages =>
                    prevImages.map(img =>
                        img.id === imageId
                            ? { ...img, multi_screen: newMultiScreenStatus }
                            : img
                    )
                );
            } else {
                message.error(response.message || `双屏素材状态修改失败`);
            }
        } catch (error: any) {
            message.error(`双屏素材状态修改失败`);
            console.error('双屏素材状态修改失败:', error);
        }
    };



    // 处理尺寸选择变化
    const handleSizeChange = (value: string | null) => {
        if (value) {
            const [width, height] = value.split('x').map(Number);
            setSelectedSize({ width, height });
        } else {
            setSelectedSize(null);
        }
        setCurrentPage(1);
    };

    // 处理标签选择变化
    const handleTagChange = (value: number | null) => {
        setSelectedTagId(value === null ? undefined : value);
        // 选择标签时，清空双屏素材筛选
        if (value !== null) {
            setSelectedMultiScreen(undefined);
        }
        setCurrentPage(1);
    };

    // 处理双屏素材选择变化
    const handleMultiScreenChange = (value: boolean | null) => {
        setSelectedMultiScreen(value === null ? undefined : value);
        // 选择双屏素材时，清空标签筛选
        if (value === true) {
            setSelectedTagId(undefined);
        }
        setCurrentPage(1);
    };

    // 处理动图状态变化
    const handleAnimatedChange = (value: boolean | null) => {
        setSelectedAnimated(value === null ? undefined : value);
        setCurrentPage(1);
    };

    // 处理分发状态变化
    const handleDistributedChange = (value: boolean | null) => {
        setSelectedDistributed(value === null ? undefined : value);
        setCurrentPage(1);
    };

    // 处理分页变化
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // 初始化加载标签、设备类型和排序类别
    useEffect(() => {
        loadTags();
        loadDeviceTypes();
        loadSortCategories();
    }, []);

    // 当筛选条件或分页变化时重新加载图片
    useEffect(() => {
        loadImages();
    }, [selectedSize, selectedTagId, selectedAnimated, selectedDistributed, selectedMultiScreen, currentPage]);

    const renderFilters = () => (
        <Card style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Title level={5} style={{ margin: 0 }}>筛选条件</Title>
                <Space>
                    {selectedSize && (
                        <Space>
                            <Button
                                type={batchSelectMode ? "primary" : "default"}
                                icon={<CheckOutlined />}
                                onClick={toggleBatchSelectMode}
                                size="small"
                            >
                                {batchSelectMode ? "退出批量选择" : "批量选择"}
                            </Button>
                            {batchSelectMode && (
                                <>
                                    <Button
                                        type="dashed"
                                        size="small"
                                        onClick={selectAllImages}
                                    >
                                        {selectedImageIds.length === images.length ? "取消全选" : "全选"}
                                    </Button>
                                    <Button
                                        type="primary"
                                        danger
                                        size="small"
                                        onClick={handleBatchDelete}
                                        disabled={selectedImageIds.length === 0}
                                    >
                                        批量删除 ({selectedImageIds.length})
                                    </Button>
                                </>
                            )}
                        </Space>
                    )}
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            setSelectedSize(null);
                            setSelectedTagId(undefined);
                            setSelectedAnimated(undefined);
                            setSelectedDistributed(undefined);
                            setSelectedMultiScreen(undefined);
                            setCurrentPage(1);
                            setBatchSelectMode(false);
                            setSelectedImageIds([]);
                        }}
                    >
                        重置筛选
                    </Button>
                </Space>
            </div>

            <Row gutter={[16, 12]}>
                <Col span={24}>
                    <Text strong style={{ fontSize: '14px' }}>图片尺寸：</Text>
                    <Radio.Group
                        style={{ marginTop: 6 }}
                        value={selectedSize ? `${selectedSize.width}x${selectedSize.height}` : null}
                        onChange={(e) => handleSizeChange(e.target.value)}
                        optionType="button"
                        size="small"
                    >
                        <Space wrap>
                            <Radio.Button value={null}>请选择尺寸</Radio.Button>
                            {deviceTypes.map(type => (
                                <Radio.Button key={`${type.id}`} value={`${type.width}x${type.height}`}>
                                    {type.description}
                                </Radio.Button>
                            ))}
                        </Space>
                    </Radio.Group>
                </Col>

                <Col span={24}>
                    <Text strong style={{ fontSize: '14px' }}>图片标签：</Text>
                    <Radio.Group
                        style={{ marginTop: 6 }}
                        value={selectedTagId === undefined ? null : selectedTagId}
                        onChange={(e) => handleTagChange(e.target.value)}
                        optionType="button"
                        size="small"
                        disabled={selectedMultiScreen === true}
                    >
                        <Space wrap>
                            <Radio.Button value={null}>不限标签</Radio.Button>
                            <Radio.Button value={0}>无标签</Radio.Button>
                            {tags.map(tag => (
                                <Radio.Button key={tag.id} value={tag.id}>
                                    {tag.remark}
                                </Radio.Button>
                            ))}
                        </Space>
                    </Radio.Group>
                    {selectedMultiScreen === true && (
                        <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                            （双屏素材筛选时不可选择标签）
                        </Text>
                    )}
                </Col>

                <Col span={12}>
                    <Text strong style={{ fontSize: '14px' }}>双屏素材：</Text>
                    <Radio.Group
                        style={{ marginTop: 6 }}
                        value={selectedMultiScreen === undefined ? null : selectedMultiScreen}
                        onChange={(e) => handleMultiScreenChange(e.target.value)}
                        optionType="button"
                        size="small"
                    >
                        <Space>
                            <Radio.Button value={null}>不限</Radio.Button>
                            <Radio.Button value={true}>双屏</Radio.Button>
                            {/* <Radio.Button value={false}>非双屏素材</Radio.Button> */}
                        </Space>
                    </Radio.Group>
                </Col>

                <Col span={12}>
                    <Text strong style={{ fontSize: '14px' }}>动图状态：</Text>
                    <Radio.Group
                        style={{ marginTop: 6 }}
                        value={selectedAnimated === undefined ? null : selectedAnimated}
                        onChange={(e) => handleAnimatedChange(e.target.value)}
                        optionType="button"
                        size="small"
                    >
                        <Space>
                            <Radio.Button value={null}>不限</Radio.Button>
                            <Radio.Button value={true}>动图</Radio.Button>
                            <Radio.Button value={false}>静图</Radio.Button>
                        </Space>
                    </Radio.Group>
                </Col>

                <Col span={12}>
                    <Text strong style={{ fontSize: '14px' }}>分发状态：</Text>
                    <Radio.Group
                        style={{ marginTop: 6 }}
                        value={selectedDistributed === undefined ? null : selectedDistributed}
                        onChange={(e) => handleDistributedChange(e.target.value)}
                        optionType="button"
                        size="small"
                    >
                        <Space>
                            <Radio.Button value={null}>不限</Radio.Button>
                            <Radio.Button value={true}>已分发</Radio.Button>
                            <Radio.Button value={false}>未分发</Radio.Button>
                        </Space>
                    </Radio.Group>
                </Col>
            </Row>
        </Card>
    );

    const renderImageGrid = () => {
        if (!selectedSize) {
            return (
                <Card>
                    <Empty
                        image={<PictureOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
                        description="请先选择图片尺寸"
                    />
                </Card>
            );
        }

        if (loading) {
            return (
                <Card>
                    <div style={{ textAlign: 'center', padding: '50px 0' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>加载中...</div>
                    </div>
                </Card>
            );
        }

        if (images.length === 0) {
            return (
                <Card>
                    <Empty
                        image={<PictureOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
                        description="暂无图片数据"
                    />
                </Card>
            );
        }

        return (
            <Card>
                <Row gutter={[12, 12]}>
                    {images.map(image => (
                        <Col key={image.id} xs={8} sm={6} md={6} lg={4} xl={4} xxl={3}>
                            <div
                                style={{
                                    position: 'relative',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    backgroundColor: '#f5f5f5',
                                    border: batchSelectMode && selectedImageIds.includes(image.id)
                                        ? '2px solid #1890ff'
                                        : '1px solid #d9d9d9',
                                    transition: 'all 0.3s',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    cursor: batchSelectMode ? 'pointer' : 'default'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                                onClick={batchSelectMode ? () => handleImageSelect(image.id) : undefined}
                            >
                                {/* 批量选择遮罩层 */}
                                {batchSelectMode && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor: selectedImageIds.includes(image.id)
                                                ? 'rgba(24, 144, 255, 0.1)'
                                                : 'rgba(0, 0, 0, 0.1)',
                                            zIndex: 10,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        {selectedImageIds.includes(image.id) && (
                                            <CheckOutlined
                                                style={{
                                                    fontSize: '24px',
                                                    color: '#1890ff',
                                                    backgroundColor: 'white',
                                                    borderRadius: '50%',
                                                    padding: '4px'
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                                {/* 图片区域 */}
                                <div style={{
                                    textAlign: 'center',
                                    padding: '0px', // 减少边距
                                    height: '80px', // 固定图片区域高度
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flex: '0 0 auto',
                                    width: '100%',
                                    position: 'relative' // 添加相对定位用于叠加层
                                }}>
                                    <Image
                                        width="100%"
                                        src={`${imageHost}${image.url}`}
                                        alt={`图片 ${image.id}`}
                                        style={{
                                            margin: '0px',
                                            width: '100%',
                                            maxHeight: '92px', // 增大图片显示
                                            objectFit: 'contain'
                                        }}
                                        placeholder={
                                            <div style={{
                                                height: '92px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <Spin />
                                            </div>
                                        }
                                        preview={{
                                            destroyOnHidden: true,
                                            imageRender: (originalNode, imageInfo) => (
                                                <div style={{
                                                    transform: 'scale(3)',
                                                }}>
                                                    <img src={`${imageInfo.image.url}`} alt={`图片 ${image.id}`} />
                                                </div>
                                            ),
                                            toolbarRender: () => null,
                                        }}
                                    />
                                    {/* 状态叠加层 */}
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        {/* 分发状态标签 */}
                                        <div
                                            style={{
                                                backgroundColor: image.is_distributed ? 'rgba(82, 196, 26, 0.7)' : 'rgba(255, 77, 79, 0.7)',
                                                padding: '2px 6px',
                                            }}
                                        >
                                            <Text
                                                style={{
                                                    fontSize: '11px',
                                                    color: 'black',
                                                    margin: 0,
                                                }}
                                            >
                                                {image.is_distributed ? '已上架' : '未上架'}
                                            </Text>
                                        </div>
                                        {/* 双屏素材状态标签 */}
                                        {image.multi_screen && (
                                            <div
                                                style={{
                                                    backgroundColor: 'rgba(24, 144, 255, 0.7)',
                                                    padding: '2px 6px',
                                                }}
                                            >
                                                <Text
                                                    style={{
                                                        fontSize: '11px',
                                                        color: 'white',
                                                        margin: 0,
                                                    }}
                                                >
                                                    双屏
                                                </Text>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* 标签显示区 - 固定高度占位 */}
                                <div style={{
                                    padding: '4px 8px',
                                    backgroundColor: '#fafafa',
                                    borderTop: '1px solid #f0f0f0',
                                    height: '32px', // 固定高度占位
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '2px',
                                    alignItems: 'flex-start',
                                    overflow: 'hidden',
                                    flex: '0 0 auto',
                                    justifyContent: 'space-between'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '2px',
                                        alignItems: 'flex-start',
                                        overflow: 'hidden',
                                        flex: '0 0 auto',
                                    }}>
                                        {image.tags && image.tags.length > 0 ? (
                                            image.tags.map(tag => (
                                                <Tag
                                                    key={tag.id}
                                                    style={{
                                                        fontSize: '10px',
                                                        padding: '0 4px',
                                                        lineHeight: '16px',
                                                        height: '16px',
                                                        margin: '1px'
                                                    }}
                                                    color="blue"
                                                >
                                                    {tag.remark}
                                                </Tag>
                                            ))
                                        ) : (
                                            <span style={{
                                                fontSize: '10px',
                                                color: '#ccc',
                                                lineHeight: '16px'
                                            }}>
                                                无标签
                                            </span>
                                        )}
                                    </div>
                                    {!batchSelectMode && (
                                        <Tooltip title="管理标签">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<TagOutlined />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleManageTags(image.id, image.tags || []);
                                                }}
                                                style={{
                                                    padding: '2px 4px',
                                                    fontSize: '12px',
                                                    height: 'auto',
                                                    lineHeight: 1,
                                                    color: '#1890ff'
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </div>

                                {/* 排序类别显示区 */}
                                <div style={{
                                    padding: '4px 8px',
                                    backgroundColor: '#f9f9f9',
                                    borderTop: '1px solid #f0f0f0',
                                    height: '20px', // 固定高度占位
                                    display: 'flex',
                                    alignItems: 'center',
                                    overflow: 'hidden',
                                    flex: '0 0 auto',
                                    justifyContent: 'space-between'
                                }}>
                                    {image.sort_category ? (
                                        <Tag
                                            style={{
                                                fontSize: '10px',
                                                padding: '0 4px',
                                                lineHeight: '14px',
                                                height: '14px',
                                                margin: '0'
                                            }}
                                            color="green"
                                        >
                                            {image.sort_category.remark}
                                        </Tag>
                                    ) : (
                                        <span style={{
                                            fontSize: '10px',
                                            color: '#ccc',
                                            lineHeight: '14px'
                                        }}>
                                            无排序
                                        </span>
                                    )}
                                    {!batchSelectMode && (
                                        <Tooltip title="修改排序类别">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<EditOutlined />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleManageSortCategory(image.id, image.sort_category || null);
                                                }}
                                                style={{
                                                    padding: '2px 4px',
                                                    fontSize: '12px',
                                                    height: 'auto',
                                                    lineHeight: 1,
                                                    color: '#52c41a'
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </div>

                                {/* 图片信息区 */}
                                <div style={{
                                    padding: '4px 8px',
                                    backgroundColor: '#f9f9f9',
                                    borderTop: '1px solid #f0f0f0',
                                    minHeight: '36px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '2px'
                                }}>
                                    <Text
                                        style={{
                                            fontSize: '11px',
                                            color: '#333',
                                            fontWeight: 500,
                                            lineHeight: '14px'
                                        }}
                                        title={image.name}
                                    >
                                        {/* {image.name.length > 15 ? `${image.name.substring(0, 15)}...` : image.name} */}
                                        {image.name}
                                    </Text>
                                    <Text
                                        style={{
                                            fontSize: '10px',
                                            color: '#999',
                                            lineHeight: '12px'
                                        }}
                                    >
                                        {image.created_at}
                                    </Text>
                                </div>

                                {/* 底部操作栏 */}
                                <div style={{
                                    padding: '4px 8px',
                                    backgroundColor: '#fff',
                                    borderTop: '1px solid #f0f0f0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginTop: 'auto' // 自动推到底部
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Text
                                            style={{
                                                fontSize: '12px',
                                                color: '#666',
                                                fontWeight: 500
                                            }}
                                        >
                                            ID: {image.id}
                                        </Text>
                                    </div>
                                    {!batchSelectMode && (
                                        <Space size="small">
                                            <Tooltip title={image.is_distributed ? "下架图片" : "上架图片"}>
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={image.is_distributed ? <CloudDownloadOutlined /> : <CloudUploadOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        void handleToggleDistribution(image.id, image.is_distributed);
                                                    }}
                                                    style={{
                                                        padding: '2px 4px',
                                                        fontSize: '12px',
                                                        height: 'auto',
                                                        lineHeight: 1,
                                                        color: image.is_distributed ? '#52c41a' : '#1890ff'
                                                    }}
                                                />
                                            </Tooltip>
                                            <Tooltip title={image.multi_screen ? "取消双屏素材" : "设为双屏素材"}>
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<DesktopOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        void handleToggleMultiScreen(image.id, image.multi_screen);
                                                    }}
                                                    style={{
                                                        padding: '2px 4px',
                                                        fontSize: '12px',
                                                        height: 'auto',
                                                        lineHeight: 1,
                                                        color: image.multi_screen ? '#1890ff' : '#999'
                                                    }}
                                                />
                                            </Tooltip>
                                            <Tooltip title="删除图片">
                                                <Button
                                                    type="text"
                                                    danger
                                                    size="small"
                                                    icon={<DeleteOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteImage(image.id);
                                                    }}
                                                    style={{
                                                        padding: '2px 4px',
                                                        fontSize: '12px',
                                                        height: 'auto',
                                                        lineHeight: 1
                                                    }}
                                                />
                                            </Tooltip>
                                        </Space>
                                    )}
                                </div>
                            </div>
                        </Col>
                    ))}
                </Row>

                {total > pageSize && (
                    <div style={{ textAlign: 'center', marginTop: 24 }}>
                        <Pagination
                            current={currentPage}
                            pageSize={pageSize}
                            total={total}
                            onChange={handlePageChange}
                            showSizeChanger={false}
                            showQuickJumper
                            showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
                        />
                    </div>
                )}
            </Card>
        );
    };

    return (
        <div>
            <Title level={2}>图片列表管理</Title>
            <Text type="secondary" style={{ marginBottom: 24, display: 'block' }}>
                根据图片尺寸和标签筛选图片，支持删除操作
            </Text>

            {renderFilters()}

            {/* 筛选状态显示 */}
            {selectedSize && (
                <Card style={{ marginBottom: 16, backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text type="success">
                            当前筛选：{selectedSize.width}×{selectedSize.height}
                            {selectedMultiScreen === true && ' - 仅双屏素材'}
                            {selectedMultiScreen === false && ' - 非双屏素材'}
                            {selectedTagId === 0 && ' - 无标签'}
                            {selectedTagId && selectedTagId > 0 && ` - ${tags.find(tag => tag.id === selectedTagId)?.remark}`}
                            {selectedAnimated !== undefined && ` - ${selectedAnimated ? '动图' : '静图'}`}
                            {selectedDistributed !== undefined && ` - ${selectedDistributed ? '已分发' : '未分发'}`}
                            &nbsp;&nbsp;({total} 张图片)
                        </Text>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => {
                                setSelectedSize(null);
                                setSelectedTagId(undefined);
                                setSelectedAnimated(undefined);
                                setSelectedDistributed(undefined);
                                setSelectedMultiScreen(undefined);
                                setCurrentPage(1);
                            }}
                        >
                            清除筛选
                        </Button>
                    </div>
                </Card>
            )}

            {renderImageGrid()}

            {/* 删除确认Modal */}
            <Modal
                title="确认删除"
                open={deleteModalOpen}
                onOk={confirmDelete}
                onCancel={cancelDelete}
                okText="确认删除"
                cancelText="取消"
                okType="danger"
                confirmLoading={deleting}
                centered
                destroyOnClose
            >
                <div style={{ padding: '16px 0' }}>
                    <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '22px', marginRight: '12px' }} />
                    <span>确定要删除图片 ID: {imageToDelete} 吗？此操作不可恢复。</span>
                </div>
            </Modal>

            {/* 标签管理Modal */}
            <Modal
                title={`管理图片标签 (ID: ${currentImageId})`}
                open={tagModalOpen}
                onCancel={closeTagModal}
                footer={[
                    <Button key="close" onClick={closeTagModal}>
                        关闭
                    </Button>
                ]}
                width={600}
                destroyOnClose
            >
                <div style={{ marginBottom: 16 }}>
                    <Text strong>当前标签：</Text>
                    <div style={{ marginTop: 8, minHeight: 32 }}>
                        {currentImageTags.length > 0 ? (
                            <Space wrap>
                                {currentImageTags.map(tag => (
                                    <Tag
                                        key={tag.id}
                                        closable
                                        onClose={() => handleRemoveTag(tag.id)}
                                        color="blue"
                                    >
                                        {tag.l10n_zh || tag.remark}
                                    </Tag>
                                ))}
                            </Space>
                        ) : (
                            <Text type="secondary">暂无标签</Text>
                        )}
                    </div>
                </div>

                <div>
                    <Text strong>添加标签：</Text>
                    <div style={{ marginTop: 8 }}>
                        <Space wrap>
                            {tags
                                .filter(tag => !currentImageTags.some(currentTag => currentTag.id === tag.id))
                                .map(tag => (
                                    <Button
                                        key={tag.id}
                                        size="small"
                                        type="dashed"
                                        icon={<PlusOutlined />}
                                        onClick={() => handleAddTag(tag.id)}
                                        loading={managingTags}
                                        style={{ borderColor: '#52c41a', color: '#52c41a' }}
                                    >
                                        {tag.l10n_zh || tag.remark}
                                    </Button>
                                ))}
                        </Space>
                        {tags.filter(tag => !currentImageTags.some(currentTag => currentTag.id === tag.id)).length === 0 && (
                            <Text type="secondary">所有标签都已添加</Text>
                        )}
                    </div>
                </div>
            </Modal>

            {/* 排序类别管理Modal */}
            <Modal
                title={`修改图片排序类别 (ID: ${currentImageId})`}
                open={sortCategoryModalOpen}
                onCancel={closeSortCategoryModal}
                footer={[
                    <Button key="close" onClick={closeSortCategoryModal}>
                        关闭
                    </Button>
                ]}
                width={500}
                destroyOnClose
            >
                <div style={{ marginBottom: 16 }}>
                    <Text strong>当前排序类别：</Text>
                    <div style={{ marginTop: 8, minHeight: 32 }}>
                        {currentImageSortCategory ? (
                            <Tag color="green" style={{ fontSize: '14px', padding: '4px 8px' }}>
                                {currentImageSortCategory.remark}
                            </Tag>
                        ) : (
                            <Text type="secondary">暂无排序类别</Text>
                        )}
                    </div>
                </div>

                <div>
                    <Text strong>选择新的排序类别：</Text>
                    <div style={{ marginTop: 8 }}>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="请选择排序类别"
                            value={currentImageSortCategory?.id}
                            onChange={handleUpdateSortCategory}
                            loading={managingSortCategory}
                        >
                            {sortCategories.map(category => (
                                <Option key={category.id} value={category.id}>
                                    {category.remark}
                                </Option>
                            ))}
                        </Select>
                    </div>
                </div>
            </Modal>

            {/* 批量删除确认Modal */}
            <Modal
                title="批量删除确认"
                open={batchDeleteModalOpen}
                onOk={confirmBatchDelete}
                onCancel={cancelBatchDelete}
                okText="确认删除"
                cancelText="取消"
                okType="danger"
                confirmLoading={batchDeleting}
                centered
                destroyOnClose
            >
                <div style={{ padding: '16px 0' }}>
                    <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '22px', marginRight: '12px' }} />
                    <span>确定要删除选中的 {selectedImageIds.length} 张图片吗？此操作不可恢复。</span>
                    <div style={{ marginTop: 12, color: '#666', fontSize: '12px' }}>
                        将要删除的图片ID：{selectedImageIds.join(', ')}
                    </div>
                </div>
            </Modal>


        </div>
    );
};

export default ImageList; 